import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import {
  ApiAuthError,
  fetchUserDetail,
  fetchUserQuota,
  resetUserQuota,
  type DashboardWindow,
} from "../api/client";
import type { QuotaInfo } from "../api/types";
import { displayUserId } from "../lib/userId";
import {
  OUTCOMES,
  type Outcome,
  type PrimaryTask,
  type UserOrigin,
  type UserTokenSeriesPoint,
} from "../api/types";
import { useToken } from "../auth/TokenContext";
import { useFilters } from "../filters/FiltersContext";

const QUOTA_COLORS = { ok: "#22c55e", warn: "#f59e0b", critical: "#ef4444" };

function quotaColor(q: QuotaInfo): string {
  if (!q.allowed) return QUOTA_COLORS.critical;
  const pct = q.spentDollars / q.budgetDollars;
  if (pct >= 0.9) return QUOTA_COLORS.critical;
  if (pct >= 0.6) return QUOTA_COLORS.warn;
  return QUOTA_COLORS.ok;
}

interface Props {
  userId: string | null;
  onClose: () => void;
  /** Called when the user clicks a row in the "recent sessions" list. */
  onSelectSession: (sessionId: string) => void;
}

const ORIGIN_LABEL: Record<UserOrigin, string> = {
  adobe: "Adobe",
  "non-adobe": "Non-Adobe",
  unknown: "Unknown",
};
const ORIGIN_COLORS: Record<UserOrigin, string> = {
  adobe: "#6366f1",
  "non-adobe": "#0ea5e9",
  unknown: "#71717a",
};

const OUTCOME_COLORS: Record<Outcome, string> = {
  completed: "#10b981",
  partial: "#f59e0b",
  exploratory: "#6366f1",
  stuck: "#ef4444",
  abandoned: "#71717a",
};

const TASK_LABELS: Record<PrimaryTask, string> = {
  page_migration: "Page migration",
  refinement: "Refinement",
  skill_authoring: "Skill authoring",
  content_generation: "Content generation",
  other: "Other",
};

function formatTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

function formatCost(usd: number): string {
  if (usd >= 1000) return `$${(usd / 1000).toFixed(1)}k`;
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd > 0) return `$${usd.toFixed(3)}`;
  return "$0";
}

function formatDuration(min: number): string {
  if (min < 1) return "<1m";
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  const remaining = min % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

function formatStartedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function TokenSeriesMiniChart({
  points,
}: {
  points: UserTokenSeriesPoint[];
}): JSX.Element {
  const option: EChartsOption = {
    grid: { left: 48, right: 48, top: 24, bottom: 32 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0];
        if (!first) return "";
        const idx = first.dataIndex as number;
        const p = points[idx];
        if (!p) return "";
        return [
          `<strong>${p.date}</strong>`,
          `Tokens: ${p.totalTokens.toLocaleString("en-US")}`,
          `Cost: ${formatCost(p.costUsd)}`,
        ].join("<br/>");
      },
    },
    xAxis: {
      type: "category",
      data: points.map((p) => p.date),
      axisLabel: { formatter: (v: string) => v.slice(5) },
    },
    yAxis: [
      {
        type: "value",
        name: "Tokens",
        nameGap: 36,
        axisLabel: { formatter: (v: number) => formatTokens(v) },
      },
      {
        type: "value",
        name: "Cost",
        nameGap: 32,
        axisLabel: { formatter: (v: number) => formatCost(v) },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: "Tokens",
        type: "bar",
        barMaxWidth: 18,
        itemStyle: { color: "#6366f1", borderRadius: [3, 3, 0, 0] },
        data: points.map((p) => p.totalTokens),
      },
      {
        name: "Cost",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbol: "none",
        lineStyle: { color: "#f59e0b", width: 2 },
        itemStyle: { color: "#f59e0b" },
        data: points.map((p) => p.costUsd),
      },
    ],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 220, width: "100%" }}
      notMerge
      lazyUpdate
    />
  );
}

function OutcomeMiniDonut({
  outcomes,
  unclassified,
}: {
  outcomes: Partial<Record<Outcome, number>>;
  unclassified: number;
}): JSX.Element {
  interface DonutSlice {
    name: string;
    value: number;
    itemStyle: { color: string };
  }
  const data: DonutSlice[] = OUTCOMES.map((outcome) => ({
    name: outcome,
    value: outcomes[outcome] ?? 0,
    itemStyle: { color: OUTCOME_COLORS[outcome] },
  })).filter((d) => d.value > 0);
  if (unclassified > 0) {
    data.push({
      name: "unclassified",
      value: unclassified,
      itemStyle: { color: "#d4d4dc" },
    });
  }
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return <div className="muted">No sessions in window.</div>;
  }
  const option: EChartsOption = {
    tooltip: {
      trigger: "item",
      formatter: (p) => {
        const pp = Array.isArray(p) ? p[0] : p;
        if (!pp) return "";
        const value = pp.value as number;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
        return `<strong>${pp.name}</strong><br/>${value} sessions (${pct}%)`;
      },
    },
    legend: { bottom: 0, icon: "circle", textStyle: { fontSize: 11 } },
    series: [
      {
        type: "pie",
        radius: ["48%", "76%"],
        center: ["50%", "42%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: "#fff", borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data,
      },
    ],
    graphic: [
      {
        type: "text",
        left: "center",
        top: "38%",
        style: {
          text: total.toString(),
          align: "center",
          fontSize: 22,
          fontWeight: 700,
          fill: "#111",
        },
      },
      {
        type: "text",
        left: "center",
        top: "49%",
        style: {
          text: "sessions",
          align: "center",
          fontSize: 11,
          fill: "#888",
        },
      },
    ],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 220, width: "100%" }}
      notMerge
      lazyUpdate
    />
  );
}

function ModelsMiniDonut({ models }: { models: string[] }): JSX.Element | null {
  if (models.length === 0) return null;
  // The drawer has per-user models as a flat list (no per-model token
  // breakdown). Render a flat list rather than a pie that lies about
  // proportions.
  return (
    <ul className="drawer-models-list">
      {models.map((m) => (
        <li key={m}>
          <code>{m}</code>
        </li>
      ))}
    </ul>
  );
}

export function UserDetailDrawer({
  userId,
  onClose,
  onSelectSession,
}: Props): JSX.Element | null {
  const { token } = useToken();
  const { range, origin } = useFilters();
  const tokenKey = token?.slice(-8);

  useEffect(() => {
    if (!userId) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [userId, onClose]);

  const detailQuery = useQuery({
    queryKey: ["user-detail", userId, range.from, range.to, tokenKey],
    queryFn: () =>
      fetchUserDetail(
        userId!,
        { from: range.from, to: range.to, origin },
        token,
      ),
    enabled: !!userId && !!token,
  });

  const queryClient = useQueryClient();
  const quotaQuery = useQuery({
    queryKey: ["user-quota", userId, tokenKey],
    queryFn: () => fetchUserQuota(userId!, token),
    enabled: !!userId && !!token,
  });

  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const handleReset = async (): Promise<void> => {
    if (!userId || !token) return;
    if (
      !window.confirm(`Reset spend quota for ${userId}? This cannot be undone.`)
    )
      return;
    setResetting(true);
    setResetError(null);
    try {
      await resetUserQuota(userId, token);
      await queryClient.invalidateQueries({ queryKey: ["user-quota", userId] });
      await queryClient.invalidateQueries({ queryKey: ["quotas"] });
    } catch (err) {
      setResetError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  if (!userId) return null;

  const detail = detailQuery.data?.user;
  const quota = quotaQuery.data;
  const isAuthError = detailQuery.error instanceof ApiAuthError;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} aria-hidden="true" />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="User detail"
      >
        <header className="drawer-header">
          <div>
            <h2>User</h2>
            <code className="drawer-session-id" title={userId ?? undefined}>
              {displayUserId(userId)}
            </code>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            title="Close (Esc)"
          >
            ✕
          </button>
        </header>

        <div className="drawer-body">
          {detailQuery.isLoading && <p className="drawer-loading">Loading…</p>}
          {detailQuery.isError && !isAuthError && (
            <p className="drawer-error">
              Failed to load: {String(detailQuery.error)}
            </p>
          )}
          {detail && (
            <>
              <section className="drawer-section">
                <div className="drawer-meta-grid">
                  <div>
                    <div className="drawer-meta-label">Origin</div>
                    <div className="drawer-meta-value">
                      <span
                        className="badge badge-outcome"
                        style={{
                          backgroundColor: `${ORIGIN_COLORS[detail.origin]}22`,
                          color: ORIGIN_COLORS[detail.origin],
                        }}
                      >
                        {ORIGIN_LABEL[detail.origin]}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Sessions</div>
                    <div className="drawer-meta-value">
                      {detail.sessions.toLocaleString("en-US")}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Tokens</div>
                    <div
                      className="drawer-meta-value"
                      title={detail.totalTokens.toLocaleString("en-US")}
                    >
                      {formatTokens(detail.totalTokens)}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Cost</div>
                    <div className="drawer-meta-value">
                      {formatCost(detail.totalCostUsd)}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Completion</div>
                    <div
                      className="drawer-meta-value"
                      title={
                        detail.completionRate === null
                          ? "No classified sessions yet"
                          : `${detail.completedSessions} / ${detail.classifiedSessions} classified`
                      }
                    >
                      {detail.completionRate === null
                        ? "—"
                        : `${Math.round(detail.completionRate * 100)}%`}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">First seen</div>
                    <div className="drawer-meta-value">
                      {detail.firstSeen
                        ? new Date(detail.firstSeen).toLocaleString("en-US")
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Last seen</div>
                    <div className="drawer-meta-value">
                      {detail.lastSeen
                        ? new Date(detail.lastSeen).toLocaleString("en-US")
                        : "—"}
                    </div>
                  </div>
                </div>
              </section>

              {quota && (
                <section className="drawer-section">
                  <h3>Spend quota (rolling 7 days)</h3>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#e5e7eb",
                      overflow: "hidden",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: 4,
                        width: `${Math.min(100, (quota.spentDollars / quota.budgetDollars) * 100)}%`,
                        backgroundColor: quotaColor(quota),
                        transition: "width 0.3s",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {formatCost(quota.spentDollars)} /{" "}
                      {formatCost(quota.budgetDollars)}
                      {!quota.allowed && (
                        <span
                          style={{
                            color: "#ef4444",
                            fontWeight: 600,
                            marginLeft: 8,
                          }}
                        >
                          BLOCKED
                        </span>
                      )}
                    </span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      Resets{" "}
                      {new Date(quota.resetsAt).toLocaleDateString("en-US")}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={resetting}
                    style={{
                      marginTop: 8,
                      padding: "4px 12px",
                      fontSize: 12,
                      border: "1px solid #ef4444",
                      borderRadius: 6,
                      color: "#ef4444",
                      backgroundColor: "transparent",
                      cursor: resetting ? "wait" : "pointer",
                      opacity: resetting ? 0.5 : 1,
                    }}
                  >
                    {resetting ? "Resetting…" : "Reset quota"}
                  </button>
                  {resetError && (
                    <p style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>
                      {resetError}
                    </p>
                  )}
                </section>
              )}

              <section className="drawer-section">
                <h3>Tokens &amp; cost (daily)</h3>
                {detail.tokenSeries.length === 0 ? (
                  <p className="muted">No tokens in window.</p>
                ) : (
                  <TokenSeriesMiniChart points={detail.tokenSeries} />
                )}
              </section>

              <section className="drawer-section">
                <h3>Outcomes</h3>
                <OutcomeMiniDonut
                  outcomes={detail.outcomes}
                  unclassified={detail.unclassifiedSessions}
                />
              </section>

              {detail.models.length > 0 && (
                <section className="drawer-section">
                  <h3>Models used</h3>
                  <ModelsMiniDonut models={detail.models} />
                </section>
              )}

              {detail.topSkills.length > 0 && (
                <section className="drawer-section">
                  <h3>Top skills loaded</h3>
                  <ul className="drawer-top-list">
                    {detail.topSkills.map((s) => (
                      <li key={s.skill}>
                        <code>{s.skill}</code>
                        <span className="muted">
                          {" "}
                          — {s.calls} call{s.calls === 1 ? "" : "s"} across{" "}
                          {s.sessions} session
                          {s.sessions === 1 ? "" : "s"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {detail.topSprinkles.length > 0 && (
                <section className="drawer-section">
                  <h3>Top sprinkles fired</h3>
                  <ul className="drawer-top-list">
                    {detail.topSprinkles.map((s) => (
                      <li key={s.channel}>
                        <code>{s.channel}</code>
                        <span className="muted">
                          {" "}
                          — {s.events} event{s.events === 1 ? "" : "s"} across{" "}
                          {s.sessions} session{s.sessions === 1 ? "" : "s"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {detail.recentSessions.length > 0 && (
                <section className="drawer-section">
                  <h3>Recent sessions</h3>
                  <ul className="drawer-recent-sessions">
                    {detail.recentSessions.map((s) => (
                      <li key={s.sessionId}>
                        <button
                          type="button"
                          className="drawer-recent-session"
                          onClick={() => onSelectSession(s.sessionId)}
                          title="Open session detail"
                        >
                          <span className="drawer-recent-session-time">
                            {formatStartedAt(s.startedAt)}
                          </span>
                          <span className="drawer-recent-session-task">
                            {s.primaryTask
                              ? TASK_LABELS[s.primaryTask]
                              : "unclassified"}
                          </span>
                          {s.outcome && (
                            <span
                              className="badge badge-outcome"
                              style={{
                                backgroundColor: `${OUTCOME_COLORS[s.outcome]}22`,
                                color: OUTCOME_COLORS[s.outcome],
                              }}
                            >
                              {s.outcome}
                            </span>
                          )}
                          <span className="muted">
                            {formatDuration(s.durationMin)}
                          </span>
                          {s.journeyHeadline && (
                            <span className="drawer-recent-session-headline">
                              {s.journeyHeadline}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
